import React from 'react';
import { Modal, Form, Select, Input, InputNumber, DatePicker, Button, Space, Popconfirm, Tag, notification } from 'antd';
import { SendOutlined, CheckOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';
import moment from 'moment';

import request from '../lib/request';
import { PdfButton, EmailButton } from './DocumentActions';

export const PROPOSAL_STATUS_COLORS = { PENDING: 'blue', SENT: 'purple', ACCEPTED: 'green', REJECTED: 'red', EXPIRED: 'orange' };
export const FREQUENCY_OPTIONS = [
  { value: 'ONE_OFF', label: 'One-off' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'ANNUALLY', label: 'Annually' },
];
export const money = (v) => (v === null || v === undefined || v === '' ? '-' : Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const toDate = (v) => (v ? moment(v) : null);
const fromDate = (v) => (v ? v.format('YYYY-MM-DD') : null);

/**
 * Create / edit one proposal (xlsx "Proposal List" fields). `target` pins it
 * to a lead or client ({ lead_id } / { client_id }); without it the caller
 * passes `targets` ({ leads, clients }) and the user picks. Pass `proposal`
 * to edit an open (PENDING / SENT) one.
 */
export default function ProposalEditorModal({ open, onClose, onSaved, target, targets, proposal }) {
  const [form] = Form.useForm();
  const [services, setServices] = React.useState([]);
  const [forType, setForType] = React.useState('client');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    request
      .get('/services')
      .then((r) => r.json())
      .then((r) => r.status && setServices(r.data));
  }, []);

  React.useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (proposal) {
      form.setFieldsValue({
        ...proposal,
        proposal_date: toDate(proposal.proposal_date),
        sent_date: toDate(proposal.sent_date),
        valid_until: toDate(proposal.valid_until),
      });
    } else {
      form.setFieldsValue({ for_type: 'client', proposal_date: moment(), valid_until: moment().add(30, 'days') });
      setForType('client');
    }
  }, [open, proposal, form]);

  const handleOk = () => {
    form.validateFields().then((values) => {
      const { for_type, for_id, ...fields } = values;
      const payload = {
        ...fields,
        proposal_date: fromDate(fields.proposal_date),
        sent_date: fromDate(fields.sent_date),
        valid_until: fromDate(fields.valid_until),
      };
      if (!proposal) {
        if (target) Object.assign(payload, target);
        else payload[for_type === 'lead' ? 'lead_id' : 'client_id'] = for_id;
      }
      setBusy(true);
      (proposal ? request.put(`/proposals/${proposal.id}`, payload) : request.post('/proposals', payload))
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: proposal ? 'Proposal updated' : `Proposal ${r.data.proposal_code} created` });
            onSaved(r.data);
            onClose();
          } else {
            notification.error({ message: r.message });
          }
        })
        .finally(() => setBusy(false));
    });
  };

  return (
    <Modal title={proposal ? `Edit ${proposal.proposal_code}` : 'New Proposal'} open={open} onCancel={onClose} onOk={handleOk} confirmLoading={busy} width={720}>
      <Form form={form} layout="vertical">
        {!target && !proposal && (
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="for_type" label="Proposal for" rules={[{ required: true }]} style={{ width: 200 }}>
              <Select
                onChange={(v) => {
                  setForType(v);
                  form.setFieldsValue({ for_id: null });
                }}
                options={[
                  { value: 'client', label: 'Client' },
                  { value: 'lead', label: 'Lead (not yet converted)' },
                ]}
              />
            </Form.Item>
            <Form.Item name="for_id" label={forType === 'lead' ? 'Lead' : 'Client'} rules={[{ required: true }]} style={{ width: 440 }}>
              <Select
                showSearch
                optionFilterProp="label"
                options={((forType === 'lead' ? targets?.leads : targets?.clients) || []).map((r) => ({
                  value: r.id,
                  label: forType === 'lead' ? r.lead_name : r.client_name,
                }))}
              />
            </Form.Item>
          </Space>
        )}
        <Space style={{ display: 'flex' }} align="start">
          <Form.Item name="service_id" label="Service" rules={[{ required: true }]} style={{ width: 300 }}>
            <Select showSearch optionFilterProp="label" options={services.map((s) => ({ value: s.id, label: s.service_name }))} />
          </Form.Item>
          <Form.Item name="title" label="Proposal name" style={{ width: 340 }} tooltip="Shown on the proposal list; defaults to the service name">
            <Input maxLength={150} />
          </Form.Item>
        </Space>
        <Space style={{ display: 'flex' }} align="start">
          <Form.Item name="cost_type" label="Cost type" style={{ width: 150 }}>
            <Select allowClear options={['FIXED', 'HOURLY'].map((v) => ({ value: v, label: v }))} />
          </Form.Item>
          <Form.Item name="frequency" label="Billing frequency" style={{ width: 160 }}>
            <Select allowClear options={FREQUENCY_OPTIONS} />
          </Form.Item>
          <Form.Item name="proposed_annual_value" label="Annual value (1)" style={{ width: 160 }} tooltip="First year, EUR excl. VAT">
            <InputNumber style={{ width: '100%' }} min={0} precision={2} />
          </Form.Item>
          <Form.Item name="proposed_annual_value_2" label="Annual value (2)" style={{ width: 160 }} tooltip="From the second year onwards, if different">
            <InputNumber style={{ width: '100%' }} min={0} precision={2} />
          </Form.Item>
        </Space>
        <Space style={{ display: 'flex' }} align="start">
          <Form.Item name="proposal_date" label="Proposal date" style={{ width: 200 }}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="sent_date" label="Date sent" style={{ width: 200 }} tooltip="Leave empty and use Send when it goes out">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="valid_until" label="Valid until" style={{ width: 200 }}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Space>
        <Form.Item name="details" label="Proposal details" tooltip="Empty = the company's proposal template is used">
          <Input.TextArea rows={8} placeholder="Scope, deliverables, fees and terms. Leave empty to start from the company's proposal template." />
        </Form.Item>
      </Form>
    </Modal>
  );
}

/** Send / Accept / Reject / Edit buttons for one proposal row; calls onChange after any action. */
export function ProposalActions({ proposal, onEdit, onChange, size = 'small' }) {
  const act = (action) => {
    request
      .post(`/proposals/${proposal.id}/${action}`, {})
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          notification.success({ message: `${proposal.proposal_code} ${r.data.status.toLowerCase()}` });
          onChange(r.data);
        } else {
          notification.error({ message: r.message });
        }
      });
  };
  const open = proposal.status === 'PENDING' || proposal.status === 'SENT';
  if (!open) return <PdfButton type="proposal" id={proposal.id} size={size} children={null} />;
  return (
    <Space size={4}>
      {onEdit && <Button size={size} icon={<EditOutlined />} onClick={() => onEdit(proposal)} title="Edit" />}
      <PdfButton type="proposal" id={proposal.id} size={size} children={null} />
      <EmailButton type="proposal" id={proposal.id} size={size} onSent={() => onChange(proposal)} children={null} />
      {proposal.status === 'PENDING' && (
        <Button size={size} icon={<SendOutlined />} onClick={() => act('send')} title="Mark as sent (without e-mailing)">
          Sent
        </Button>
      )}
      <Popconfirm title="Mark this proposal as accepted by the client?" onConfirm={() => act('accept')}>
        <Button size={size} type="primary" ghost icon={<CheckOutlined />}>
          Accept
        </Button>
      </Popconfirm>
      <Popconfirm title="Mark this proposal as rejected?" onConfirm={() => act('reject')}>
        <Button size={size} danger icon={<CloseOutlined />}>
          Reject
        </Button>
      </Popconfirm>
    </Space>
  );
}

export function ProposalStatusTag({ status }) {
  return <Tag color={PROPOSAL_STATUS_COLORS[status]}>{status}</Tag>;
}

/** Columns shared by every proposal table; `extra` are appended before actions. */
export function proposalColumns({ extra = [], onEdit, onChange, withFor }) {
  return [
    { title: 'Code', dataIndex: 'proposal_code', width: 130 },
    ...(withFor ? [withFor] : []),
    { title: 'Proposal', key: 'title', render: (_, p) => p.title || p.Service?.service_name || '-' },
    { title: 'Service', key: 'service', render: (_, p) => p.Service?.service_name || '-' },
    { title: 'Annual value (1)', dataIndex: 'proposed_annual_value', align: 'right', render: money },
    { title: 'Annual value (2)', dataIndex: 'proposed_annual_value_2', align: 'right', render: money },
    { title: 'Sent', dataIndex: 'sent_date', render: (v) => v || '-' },
    { title: 'Valid until / decided', key: 'dates', render: (_, p) => (p.decision_date ? `${p.decision_date} (${p.status.toLowerCase()})` : p.valid_until || '-') },
    { title: 'Status', dataIndex: 'status', render: (v) => <ProposalStatusTag status={v} /> },
    ...extra,
    { title: '', key: 'actions', render: (_, p) => <ProposalActions proposal={p} onEdit={onEdit} onChange={onChange} /> },
  ];
}
